import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpHeaders,
  HttpErrorResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { LooseObject } from '../../models/common/loose-object.model';
import { StorageService } from '../../store/storage/storage.service';
import { AuthService } from '../../services/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private redirecting = false;

  constructor(
    private storageService: StorageService,
    private injector: Injector
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isAuthReq = this.isAuthRequired(request.url.toString());
    const hasSession = this.storageService.hasStoredSession();

    if (!isAuthReq || !hasSession) {
      const preparedReq = this.prepareRequest(request);
      return next.handle(preparedReq);
    }

    const currentToken = this.storageService.getUserData()?.token;

    // If token is still current, proceed with the token
    if (currentToken && this.storageService.isTokenCurrent(currentToken)) {
      const authReq = this.prepareRequest(request, currentToken);
      return next.handle(authReq).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(request, next);
          }
          return throwError(() => error);
        })
      );
    }

    // Token is expired; proactively refresh and queue pending requests
    return this.handle401Error(request, next);
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Prevent infinite loop if already retried with fresh token and still 401
    if (request.headers.has('X-VM-Retried')) {
      this.forceLogout();
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const authService = this.injector.get(AuthService);

      return from(authService.refreshToken(true)).pipe(
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          const retriedReq = this.prepareRequest(request, newToken, true);
          return next.handle(retriedReq);
        }),
        catchError((refreshError) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.forceLogout();
          return throwError(() => refreshError);
        })
      );
    } else {
      // Refresh in progress: queue request until new token is emitted
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap((newToken: string) => {
          const retriedReq = this.prepareRequest(request, newToken, true);
          return next.handle(retriedReq);
        })
      );
    }
  }

  private forceLogout(): void {
    if (this.redirecting) {
      return;
    }
    this.redirecting = true;
    this.storageService.clearAuthSession();
    const router = this.injector.get(Router);
    if (!router.url.startsWith('/auth/')) {
      router.navigateByUrl('/auth/login').finally(() => {
        this.redirecting = false;
      });
    } else {
      this.redirecting = false;
    }
  }

  private prepareRequest(request: HttpRequest<any>, token?: string, isRetry = false): HttpRequest<any> {
    let headers: LooseObject = {};
    const dataType = this.setDataType(request.url.toString(), request);

    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }
    if (isRetry) {
      headers['X-VM-Retried'] = 'true';
    }

    switch (dataType) {
      case 'text':
        headers['Content-Type'] = 'text/plain';
        break;
      case 'byte':
        headers['Content-Type'] = 'application/octet-stream';
        break;
      case 'xml':
        headers['Content-Type'] = 'application/xml';
        break;
      case 'csv':
        headers['Content-Type'] = 'text/csv';
        break;
      case 'binary':
        headers['Content-Type'] = undefined;
        break;
      case 'cloud-event':
        headers['Content-Type'] = 'application/cloudevents+json';
        break;
      default:
        headers['Content-Type'] = 'application/json';
        break;
    }

    const requestHeaders = this.getRequestHeaders(request);
    headers = Object.assign(requestHeaders, headers);

    return request.clone({
      headers: new HttpHeaders(headers),
      params: request.params
    });
  }

  private getRequestHeaders(request: HttpRequest<any>) {
    const headerKeys = request.headers.keys();
    let requestHeaders: LooseObject = {};
    headerKeys.forEach((key: string) => {
      requestHeaders[key] = request.headers.get(key);
    });
    return requestHeaders;
  }

  private setDataType(requestUrl: string, request: HttpRequest<any>) {
    return 'json';
  }

  private isAuthRequired(requestUrl: string): boolean {
    const publicEndpoints = [
      '/api/Stylists/StylistLoginUsingOTP',
      '/api/Stylists/CheckOTP',
      '/api/Clients/GenerateCustomToken'
    ];
    return !publicEndpoints.some(endpoint => requestUrl.includes(endpoint));
  }

  getApiToken(): string {
    const token = this.storageService.getUserData()?.token;
    return token && this.storageService.isTokenCurrent(token) ? token : '';
  }
}

